// Defense-in-depth guard for backend-supplied redirect URLs (br-k6c).
//
// Both the invoice "pay" flow and the payment-method setup flow navigate the
// browser to a URL that comes from authenticated backend JSON. That URL is
// trusted today, but a future backend bug (or a compromised upstream payment
// provider response) could turn an unvalidated `window.location.href = url`
// into an open-redirect / redirect-to-phishing primitive. This module
// validates such URLs before navigation so the frontend never blindly follows
// an arbitrary host.

// Allowlist of payment-provider hosts we are willing to redirect to.
//
// Keep this list in one place so it is trivial to extend when the active
// payment provider changes or adds hosted-page domains.
//
// Dwolla uses embedded IAV (no browser redirect to dwolla.com), so it is NOT
// listed here -- removing it closes the open-redirect surface (HIGH-2).
// Stripe (Spec 037): Express Connect onboarding links are served from
//   connect.stripe.com; hosted invoice / payment pages from stripe.com and
//   its subdomains (e.g. checkout.stripe.com).
//
// NOTE: Each entry covers the root domain and all subdomains thereof (see
// the hostname matching logic below). Add an entry only when the backend is
// confirmed to return that host in a redirect payload.
const ALLOWED_REDIRECT_HOSTS: readonly string[] = [
  // Stripe Express Connect onboarding (Spec 037, PAYMENT_PROVIDER=stripe).
  'connect.stripe.com',
  'stripe.com',
];

/**
 * Returns true if `rawUrl` is a well-formed absolute URL that is safe to
 * navigate to: it must use HTTPS and its host must be an allowlisted
 * payment-provider host (exact match or a subdomain thereof).
 */
export function isSafeRedirectUrl(rawUrl: unknown): rawUrl is string {
  if (typeof rawUrl !== 'string' || rawUrl.length === 0) return false;

  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'https:') return false;

  const host = parsed.hostname.toLowerCase();
  return ALLOWED_REDIRECT_HOSTS.some(
    (allowed) => host === allowed || host.endsWith(`.${allowed}`),
  );
}
